import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Trash2, Upload, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls =
    "w-full border border-white/15 bg-navy-deep px-4 py-3 text-sm text-[#F8F9FA] focus:border-gold focus:outline-none transition-colors";
const btnCls = "flex items-center gap-2 border border-white/15 px-3 py-2 text-[10px] uppercase tracking-[0.2em] transition-colors hover:border-gold disabled:opacity-40";

const errMsg = (err, fallback) => {
    const d = err?.response?.data?.detail;
    return Array.isArray(d) ? d.map((x) => x.msg).join(" · ") : d || fallback;
};

export default function HeroImagesPanel({ token }) {
    const [data, setData] = useState(null);
    const [alt, setAlt] = useState("");
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [interval, setIntervalSec] = useState(30);
    const fileRef = useRef(null);
    const auth = { headers: { Authorization: `Bearer ${token}` } };

    useEffect(() => {
        axios.get(`${API}/hero`).then((r) => {
            setData(r.data);
            setIntervalSec(r.data.interval_seconds);
        });
    }, []);

    const upload = async (e) => {
        e.preventDefault();
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("alt", alt);
            const { data: d } = await axios.post(`${API}/hero/slides`, fd, auth);
            setData(d);
            setAlt("");
            setFile(null);
            if (fileRef.current) fileRef.current.value = "";
            toast.success("Imagen agregada al carrusel");
        } catch (err) {
            toast.error("No se pudo subir la imagen", { description: errMsg(err, "Intenta con otra imagen.") });
        } finally {
            setUploading(false);
        }
    };

    const move = async (i, dir) => {
        const ids = data.slides.map((s) => s.id);
        const j = i + dir;
        if (j < 0 || j >= ids.length) return;
        [ids[i], ids[j]] = [ids[j], ids[i]];
        const { data: d } = await axios.put(`${API}/hero/slides/order`, { ids }, auth);
        setData(d);
    };

    const remove = async (slide) => {
        if (!window.confirm(`¿Eliminar la imagen "${slide.alt}" del carrusel?`)) return;
        try {
            const { data: d } = await axios.delete(`${API}/hero/slides/${slide.id}`, auth);
            setData(d);
            toast.success("Imagen eliminada");
        } catch (err) {
            toast.error("No se pudo eliminar", { description: errMsg(err, "") });
        }
    };

    const saveInterval = async () => {
        try {
            const { data: d } = await axios.put(`${API}/hero/settings`, { interval_seconds: Number(interval) }, auth);
            setData(d);
            toast.success(`Intervalo actualizado a ${d.interval_seconds} s`);
        } catch (err) {
            toast.error("Intervalo inválido", { description: errMsg(err, "Debe estar entre 5 y 120 segundos.") });
        }
    };

    if (!data) return <p className="mt-8 text-sm text-[#9CA3AF]">Cargando…</p>;

    return (
        <div className="mx-auto mt-8 max-w-[1440px]" data-testid="hero-images-panel">
            <div className="border-b border-white/10 pb-6">
                <h2 className="font-display text-2xl font-medium tracking-tight">Imágenes del inicio</h2>
                <p className="mt-1 text-sm text-[#9CA3AF]" data-testid="hero-images-count">
                    {data.slides.length} imagen{data.slides.length === 1 ? "" : "es"} en el carrusel · cambia cada {data.interval_seconds} s
                </p>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
                <form onSubmit={upload} className="space-y-4 border border-white/10 p-6" data-testid="hero-upload-form">
                    <p className="text-[11px] uppercase tracking-[0.3em] text-gold">Agregar imagen</p>
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                        className="block w-full text-sm text-[#9CA3AF] file:mr-4 file:border file:border-white/20 file:bg-transparent file:px-4 file:py-2 file:text-[10px] file:uppercase file:tracking-[0.2em] file:text-[#F8F9FA]"
                        data-testid="hero-upload-file"
                    />
                    <input
                        value={alt}
                        onChange={(e) => setAlt(e.target.value)}
                        required
                        minLength={3}
                        placeholder="Descripción de la imagen (texto alternativo)"
                        className={inputCls}
                        data-testid="hero-upload-alt"
                    />
                    <p className="text-[11px] text-[#9CA3AF]/70">JPG, PNG o WebP · mínimo 1200 px de ancho · máx. 12 MB. Se recorta y comprime automáticamente.</p>
                    <button type="submit" disabled={!file || uploading} className="flex w-full items-center justify-center gap-2 bg-gold px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-navy hover:bg-gold-soft disabled:opacity-50" data-testid="hero-upload-submit">
                        <Upload size={14} /> {uploading ? "Subiendo…" : "Subir imagen"}
                    </button>

                    <div className="mt-6 border-t border-white/10 pt-6">
                        <label htmlFor="hero-interval" className="mb-2 block text-[10px] uppercase tracking-[0.25em] text-[#9CA3AF]">Intervalo (segundos)</label>
                        <div className="flex gap-2">
                            <input id="hero-interval" type="number" min={5} max={120} value={interval} onChange={(e) => setIntervalSec(e.target.value)} className={inputCls} data-testid="hero-interval-input" />
                            <button type="button" onClick={saveInterval} className={btnCls} data-testid="hero-interval-save">
                                <Clock size={14} /> Guardar
                            </button>
                        </div>
                    </div>
                </form>

                <ul className="space-y-3 lg:col-span-2" data-testid="hero-slides-list">
                    {data.slides.map((s, i) => (
                        <li key={s.id} className="flex items-center gap-4 border border-white/10 p-3" data-testid={`hero-slide-item-${i}`}>
                            <img src={s.url} alt={s.alt} className="h-16 w-28 shrink-0 object-cover" loading="lazy" />
                            <div className="min-w-0 flex-1">
                                <p className="truncate text-sm">{s.alt}</p>
                                <p className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF]">Posición {i + 1}</p>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => move(i, -1)} disabled={i === 0} className={btnCls} aria-label="Subir" data-testid={`hero-slide-up-${i}`}><ArrowUp size={14} /></button>
                                <button onClick={() => move(i, 1)} disabled={i === data.slides.length - 1} className={btnCls} aria-label="Bajar" data-testid={`hero-slide-down-${i}`}><ArrowDown size={14} /></button>
                                <button onClick={() => remove(s)} className={`${btnCls} hover:border-red-400 hover:text-red-400`} aria-label="Eliminar" data-testid={`hero-slide-delete-${i}`}><Trash2 size={14} /></button>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}
